// pages/Agents/Workflows/workflowTemplates.js
// Predefined templates for both AI Agent goals and Workflow Pipeline steps.
// All templates operate on existing user-created data (no auto-creation).

// ── Professional HTML email body templates mapped by previous action type ──

const _wrap = (gradient, icon, title, subtitle, bodyHtml) => `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Oxygen,Ubuntu,sans-serif;max-width:640px;margin:0 auto;background:#ffffff;border:1px solid #e2e8f0;border-radius:16px;overflow:hidden;">
  <div style="background:${gradient};padding:40px 32px;text-align:center;">
    <div style="font-size:36px;margin-bottom:8px;">${icon}</div>
    <h1 style="margin:0;font-size:22px;font-weight:700;color:#ffffff;letter-spacing:-0.3px;">${title}</h1>
    ${subtitle ? `<p style="margin:10px 0 0;font-size:13px;color:rgba(255,255,255,0.85);">${subtitle}</p>` : ""}
  </div>
  <div style="padding:32px;">${bodyHtml}</div>
  <div style="padding:20px 32px;background:#f8fafc;border-top:1px solid #e2e8f0;text-align:center;">
    <p style="margin:0;font-size:11px;color:#94a3b8;">Automated report &middot; Klonit AI Workflow Engine</p>
  </div>
</div>`;

const _metricCard = (label, value, color) =>
  `<td style="width:33%;background:#ffffff;border:1px solid #e2e8f0;border-top:3px solid ${color};border-radius:12px;padding:18px 12px;text-align:center;">
    <div style="font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:0.6px;font-weight:600;">${label}</div>
    <div style="font-size:28px;font-weight:800;color:${color};margin:6px 0 0;">${value}</div>
  </td>`;

const _section = (title, content, color = "#334155") =>
  `<div style="margin-bottom:24px;">
    <h3 style="margin:0 0 10px;font-size:14px;font-weight:700;color:${color};text-transform:uppercase;letter-spacing:0.5px;">${title}</h3>
    ${content}
  </div>`;

const _pill = (text, bg, color) =>
  `<span style="display:inline-block;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;background:${bg};color:${color};margin:3px 4px 3px 0;">${text}</span>`;

const _summaryBox = (content) =>
  `<div style="margin-top:28px;background:linear-gradient(135deg,#f0f9ff,#eff6ff);border:1px solid #bfdbfe;border-radius:12px;padding:20px 24px;">
    <h3 style="margin:0 0 10px;font-size:14px;font-weight:700;color:#1e40af;text-transform:uppercase;letter-spacing:0.5px;">Summary</h3>
    <p style="margin:0;font-size:13px;line-height:1.8;color:#334155;">${content}</p>
  </div>`;

export const EMAIL_BODY_BY_ACTION = {
  // ── Survey Sentiment ──────────────────────────────────────────────
  "survey.trigger_sentiment": _wrap(
    "linear-gradient(135deg,#6366f1,#8b5cf6)", "📊", "Sentiment Analysis Report",
    "{{prev_output.total_analyzed}} responses analyzed across {{prev_output.questions_analyzed}} questions",
    `<div>{{prev_output._report_html}}</div>`
  ),

  // ── Survey Get Sentiment ──────────────────────────────────────────
  "survey.get_sentiment": _wrap(
    "linear-gradient(135deg,#8b5cf6,#a78bfa)", "📈", "Sentiment Results",
    "{{prev_output.total_responses}} total responses &middot; AI Predicted Score: {{prev_output.avg_sentiment_score}}",
    `<div>{{prev_output._report_html}}</div>`
  ),

  // ── Survey Analytics ──────────────────────────────────────────────
  "survey.get_analytics": _wrap(
    "linear-gradient(135deg,#0ea5e9,#6366f1)", "📋", "Survey Analytics Report",
    "{{prev_output.title}}",
    `<div>{{prev_output._report_html}}</div>
    ${_summaryBox("Survey \"{{prev_output.title}}\" has received {{prev_output.total_responses}} total responses. {{prev_output.completed_responses}} completed, {{prev_output.abandoned_responses}} abandoned, and {{prev_output.in_progress_responses}} still in progress. Completion rate is {{prev_output.completion_rate}}%.")}`
  ),

  // ── Survey Filter by Sentiment ────────────────────────────────────
  "survey.filter_by_sentiment": _wrap(
    "linear-gradient(135deg,#f59e0b,#d97706)", "💬", "We Value Your Feedback",
    "{{prev_output.count}} responses matched",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Matched", "{{prev_output.count}}", "#f59e0b")}
    </tr></table>
    <p style="font-size:14px;line-height:1.8;color:#334155;margin:0 0 16px;">
      We noticed some concerns in your responses and would love to hear more.
      Please reply to this email or contact us so we can address your feedback.
    </p>
    ${_section("Filtered Responses", `<div>{{prev_output.items}}</div>`)}
    <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:16px;text-align:center;margin-top:20px;">
      <p style="margin:0;font-size:14px;font-weight:700;color:#92400e;">Your satisfaction matters to us.</p>
    </div>
    ${_summaryBox("We identified {{prev_output.count}} responses matching the selected sentiment filter. Our team is reviewing these responses to improve your experience.")}`
  ),

  // ── Volunteer Operations ──────────────────────────────────────────
  "volunteer.list": _wrap(
    "linear-gradient(135deg,#10b981,#059669)", "👥", "Volunteer Report",
    "{{prev_output.count}} volunteers found",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Total Found", "{{prev_output.count}}", "#10b981")}
    </tr></table>
    ${_section("Volunteers", `<div>{{prev_output.items}}</div>`)}
    ${_summaryBox("Volunteer listing complete. {{prev_output.count}} volunteers matched the requested criteria.")}`
  ),

  "volunteer.bulk_approve": _wrap(
    "linear-gradient(135deg,#10b981,#059669)", "✅", "Volunteers Approved!",
    "{{prev_output.approved_count}} volunteers approved",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Approved", "{{prev_output.approved_count}}", "#10b981")}
    </tr></table>
    ${_section("Approved Volunteers", `<div>{{prev_output.volunteers}}</div>`)}
    <div style="background:#ecfdf5;border:1px solid #a7f3d0;border-radius:10px;padding:16px;text-align:center;margin-top:20px;">
      <p style="margin:0;font-size:14px;font-weight:700;color:#065f46;">All selected volunteers are now approved.</p>
    </div>
    ${_summaryBox("Bulk approval completed successfully. {{prev_output.approved_count}} volunteers have been moved to active status and are ready to be assigned to events.")}`
  ),

  "volunteer.assign_to_event": _wrap(
    "linear-gradient(135deg,#10b981,#0d9488)", "🎉", "Welcome — You're Assigned!", null,
    `<p style="font-size:14px;line-height:1.8;color:#334155;margin:0 0 16px;">
      Congratulations! You have been approved as a volunteer and assigned to the event.
    </p>
    ${_section("Assignment Details", `<div style="font-size:13px;line-height:1.8;color:#475569;">
      <div style="margin-bottom:8px;">
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Volunteer</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.volunteer_name}}</span>
      </div>
      <div style="margin-bottom:8px;">
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Event</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.event_name}}</span>
      </div>
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Role</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.role}}</span>
      </div>
    </div>`)}
    <div style="background:#ecfdf5;border:1px solid #a7f3d0;border-radius:10px;padding:16px;text-align:center;margin-top:20px;">
      <p style="margin:0;font-size:14px;font-weight:700;color:#065f46;">See you at the event!</p>
    </div>
    ${_summaryBox("You have been successfully approved and assigned to the event. Please check the event details and prepare accordingly. We're excited to have you on the team!")}`
  ),

  // ── Event Operations ──────────────────────────────────────────────
  "event.get": _wrap(
    "linear-gradient(135deg,#f97316,#ea580c)", "📅", "Event Details",
    "{{prev_output.name}}",
    `${_section("Event Information", `<div style="font-size:13px;line-height:2;color:#475569;">
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Event Name</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.name}}</span>
      </div>
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Type</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.event_type}}</span>
      </div>
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Location</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.location}}</span>
      </div>
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Start Date</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.event_start_date}}</span>
      </div>
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">End Date</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.event_end_date}}</span>
      </div>
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Status</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.status}}</span>
      </div>
    </div>`)}
    ${_summaryBox("Event details have been retrieved. Review the information above and coordinate with your team for the upcoming event.")}`
  ),

  "event.outreach_campaign": _wrap(
    "linear-gradient(135deg,#f97316,#ea580c)", "📣", "Outreach Campaign Launched", null,
    `${_section("Campaign Details", `<div style="font-size:13px;line-height:2;color:#475569;">
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Campaign</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.campaign_name}}</span>
      </div>
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Campaign ID</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.campaign_id}}</span>
      </div>
    </div>`)}
    <p style="font-size:14px;line-height:1.8;color:#334155;margin:0 0 16px;">
      The outreach campaign has been successfully launched! Invitations are being sent to all selected participants via email and SMS.
    </p>
    <div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:16px;text-align:center;margin-top:20px;">
      <p style="margin:0;font-size:14px;font-weight:700;color:#9a3412;">Campaign is now live.</p>
    </div>
    ${_summaryBox("The outreach campaign has been launched. Email and SMS invitations are being delivered to all selected participants. Monitor responses in the campaign dashboard.")}`
  ),

  // ── CRM Leads ─────────────────────────────────────────────────────
  "crm.list_leads": _wrap(
    "linear-gradient(135deg,#3b82f6,#1d4ed8)", "🎯", "Lead Report",
    "{{prev_output.count}} leads found",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Total Leads", "{{prev_output.count}}", "#3b82f6")}
    </tr></table>
    ${_section("Leads", `<div>{{prev_output.items}}</div>`)}
    ${_summaryBox("Lead listing complete. {{prev_output.count}} leads matched the requested criteria. Review and follow up to convert them into opportunities.")}`
  ),

  "crm.update_lead": _wrap(
    "linear-gradient(135deg,#3b82f6,#1d4ed8)", "📝", "Lead Updated",
    "Lead #{{prev_output.lead_id}} updated",
    `${_section("Lead Details", `<div style="font-size:13px;line-height:2;color:#475569;">
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Lead ID</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.lead_id}}</span>
      </div>
    </div>`)}
    <p style="font-size:14px;line-height:1.8;color:#334155;margin:0 0 16px;">
      Thank you for reaching out. A member of our team will be in touch shortly to discuss next steps.
    </p>
    <div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:10px;padding:16px;text-align:center;margin-top:20px;">
      <p style="margin:0;font-size:14px;font-weight:700;color:#1e40af;">We'll follow up soon.</p>
    </div>
    ${_summaryBox("Lead has been updated successfully. Our team is reviewing the information and will follow up with next steps shortly.")}`
  ),

  // ── CRM Tickets ───────────────────────────────────────────────────
  "crm.list_tickets": _wrap(
    "linear-gradient(135deg,#6366f1,#4f46e5)", "🎫", "Support Tickets Report",
    "{{prev_output.count}} tickets found",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Tickets Found", "{{prev_output.count}}", "#6366f1")}
    </tr></table>
    ${_section("Tickets", `<div>{{prev_output.items}}</div>`)}
    ${_summaryBox("Ticket listing complete. {{prev_output.count}} tickets require attention. Prioritize and assign to team members for resolution.")}`
  ),

  "crm.update_ticket": _wrap(
    "linear-gradient(135deg,#6366f1,#4f46e5)", "🔔", "Your Ticket is Being Reviewed",
    "Ticket #{{prev_output.ticket_id}} updated",
    `${_section("Ticket Details", `<div style="font-size:13px;line-height:2;color:#475569;">
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Ticket ID</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.ticket_id}}</span>
      </div>
    </div>`)}
    <p style="font-size:14px;line-height:1.8;color:#334155;margin:0 0 16px;">
      We are actively working on your support request. Our team is reviewing your ticket and we will update you shortly.
    </p>
    <div style="background:#eef2ff;border:1px solid #c7d2fe;border-radius:10px;padding:16px;text-align:center;margin-top:20px;">
      <p style="margin:0;font-size:14px;font-weight:700;color:#3730a3;">We'll get back to you as soon as possible.</p>
    </div>
    ${_summaryBox("Your support ticket has been updated. Our team is actively investigating your request and will provide an update as soon as we have more information.")}`
  ),

  // ── CRM Contacts ──────────────────────────────────────────────────
  "crm.list_contacts": _wrap(
    "linear-gradient(135deg,#0ea5e9,#0284c7)", "📇", "Contact Report",
    "{{prev_output.count}} contacts found",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Total Contacts", "{{prev_output.count}}", "#0ea5e9")}
    </tr></table>
    ${_section("Contacts", `<div>{{prev_output.items}}</div>`)}
    ${_summaryBox("Contact listing complete. {{prev_output.count}} contacts matched the requested criteria.")}`
  ),

  "crm.update_contact": _wrap(
    "linear-gradient(135deg,#0ea5e9,#0284c7)", "📇", "Contact Updated",
    "Contact #{{prev_output.contact_id}} updated",
    `${_section("Contact Details", `<div style="font-size:13px;line-height:2;color:#475569;">
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Contact ID</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.contact_id}}</span>
      </div>
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Name</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.first_name}} {{prev_output.last_name}}</span>
      </div>
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Email</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.email}}</span>
      </div>
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Status</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.status}}</span>
      </div>
    </div>`)}
    <p style="font-size:14px;line-height:1.8;color:#334155;margin:0 0 16px;">
      Your contact information has been updated. Thank you for keeping your details current.
    </p>
    ${_summaryBox("Contact information has been updated successfully. All changes are now reflected in the system.")}`
  ),

  // ── Bulk Update Actions ────────────────────────────────────────────
  "volunteer.bulk_update": _wrap(
    "linear-gradient(135deg,#10b981,#059669)", "👥", "Volunteers Updated",
    "{{prev_output.updated_count}} volunteers updated",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Updated", "{{prev_output.updated_count}}", "#10b981")}
    </tr></table>
    ${_section("Updated Volunteers", `<div>{{prev_output._items_html}}</div>`)}
    ${_summaryBox("Bulk volunteer update completed. {{prev_output.updated_count}} volunteers have been updated to the new status.")}`
  ),

  "event.bulk_update": _wrap(
    "linear-gradient(135deg,#f97316,#ea580c)", "📅", "Events Updated",
    "{{prev_output.updated_count}} events updated",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Updated", "{{prev_output.updated_count}}", "#f97316")}
    </tr></table>
    ${_section("Updated Events", `<div>{{prev_output._items_html}}</div>`)}
    ${_summaryBox("Bulk event update completed. {{prev_output.updated_count}} events have been updated to the new status.")}`
  ),

  "crm.bulk_update_leads": _wrap(
    "linear-gradient(135deg,#3b82f6,#1d4ed8)", "🎯", "Leads Bulk Updated",
    "{{prev_output.updated_count}} leads updated",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Updated", "{{prev_output.updated_count}}", "#3b82f6")}
    </tr></table>
    ${_section("Updated Leads", `<div>{{prev_output._items_html}}</div>`)}
    ${_summaryBox("Bulk lead update completed. {{prev_output.updated_count}} leads have been updated successfully.")}`
  ),

  "crm.bulk_update_contacts": _wrap(
    "linear-gradient(135deg,#0ea5e9,#0284c7)", "📇", "Contacts Bulk Updated",
    "{{prev_output.updated_count}} contacts updated",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Updated", "{{prev_output.updated_count}}", "#0ea5e9")}
    </tr></table>
    ${_section("Updated Contacts", `<div>{{prev_output._items_html}}</div>`)}
    ${_summaryBox("Bulk contact update completed. {{prev_output.updated_count}} contacts have been updated successfully.")}`
  ),

  "crm.bulk_update_tickets": _wrap(
    "linear-gradient(135deg,#6366f1,#4f46e5)", "🎫", "Tickets Bulk Updated",
    "{{prev_output.updated_count}} tickets updated",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Updated", "{{prev_output.updated_count}}", "#6366f1")}
    </tr></table>
    ${_section("Updated Tickets", `<div>{{prev_output._items_html}}</div>`)}
    ${_summaryBox("Bulk ticket update completed. {{prev_output.updated_count}} tickets have been updated successfully.")}`
  ),

  "crm.bulk_update_opportunities": _wrap(
    "linear-gradient(135deg,#8b5cf6,#7c3aed)", "💼", "Opportunities Bulk Updated",
    "{{prev_output.updated_count}} opportunities updated",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Updated", "{{prev_output.updated_count}}", "#8b5cf6")}
    </tr></table>
    ${_section("Updated Opportunities", `<div>{{prev_output._items_html}}</div>`)}
    ${_summaryBox("Bulk opportunity update completed. {{prev_output.updated_count}} opportunities have been advanced to the new stage.")}`
  ),

  "survey.bulk_update": _wrap(
    "linear-gradient(135deg,#6366f1,#8b5cf6)", "📋", "Surveys Bulk Updated",
    "{{prev_output.updated_count}} surveys updated",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Updated", "{{prev_output.updated_count}}", "#6366f1")}
    </tr></table>
    ${_section("Updated Surveys", `<div>{{prev_output._items_html}}</div>`)}
    ${_summaryBox("Bulk survey update completed. {{prev_output.updated_count}} surveys have been updated to the new status.")}`
  ),

  // ── Chat Operations ──────────────────────────────────────────────────
  "chat.list_history": _wrap(
    "linear-gradient(135deg,#14b8a6,#0d9488)", "💬", "Chat History Report",
    "{{prev_output.count}} conversations found",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Total Found", "{{prev_output.count}}", "#14b8a6")}
    </tr></table>
    ${_section("Conversations", `<div>{{prev_output.items}}</div>`)}
    ${_summaryBox("Chat history listing complete. {{prev_output.count}} conversations matched the requested criteria.")}`
  ),

  "chat.list_feedbacks": _wrap(
    "linear-gradient(135deg,#f97316,#ea580c)", "📝", "User Feedback Report",
    "{{prev_output.count}} feedbacks found",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Total Feedbacks", "{{prev_output.count}}", "#f97316")}
    </tr></table>
    ${_section("Feedbacks", `<div>{{prev_output.items}}</div>`)}
    ${_summaryBox("User feedback listing complete. {{prev_output.count}} feedbacks matched the requested criteria.")}`
  ),

  "chat.trigger_history_sentiment": _wrap(
    "linear-gradient(135deg,#14b8a6,#0d9488)", "📊", "Chat Sentiment Analysis Report",
    "{{prev_output.total_analyzed}} conversations analyzed",
    `<div>{{prev_output._report_html}}</div>`
  ),

  "chat.trigger_feedback_sentiment": _wrap(
    "linear-gradient(135deg,#f97316,#ea580c)", "📊", "Feedback Sentiment Analysis Report",
    "{{prev_output.total_analyzed}} feedbacks analyzed",
    `<div>{{prev_output._report_html}}</div>`
  ),

  "chat.get_sentiment": _wrap(
    "linear-gradient(135deg,#8b5cf6,#a78bfa)", "📈", "Chat Sentiment Results",
    "{{prev_output.total_responses}} total conversations &middot; AI Predicted Score: {{prev_output.avg_sentiment_score}}",
    `<div>{{prev_output._report_html}}</div>`
  ),

  "chat.get_analytics": _wrap(
    "linear-gradient(135deg,#14b8a6,#0d9488)", "📋", "Chat Analytics Report",
    "{{prev_output.total_qa_pairs}} Q&A pairs across {{prev_output.total_sessions}} sessions",
    `<div>{{prev_output._report_html}}</div>
    ${_summaryBox("Chat analytics summary: {{prev_output.total_qa_pairs}} Q&A pairs, {{prev_output.total_sessions}} sessions, {{prev_output.trained_count}} trained, {{prev_output.total_feedbacks}} feedbacks received.")}`
  ),

  "chat.filter_by_sentiment": _wrap(
    "linear-gradient(135deg,#f59e0b,#d97706)", "💬", "Chat Sentiment Filter Results",
    "{{prev_output.count}} conversations matched",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Matched", "{{prev_output.count}}", "#f59e0b")}
    </tr></table>
    ${_section("Filtered Conversations", `<div>{{prev_output.items}}</div>`)}
    ${_summaryBox("Found {{prev_output.count}} conversations matching the selected sentiment filter.")}`
  ),

  "survey.bulk_sentiment": _wrap(
    "linear-gradient(135deg,#6366f1,#8b5cf6)", "📊", "Bulk Sentiment Analysis Report",
    "{{prev_output.surveys_analyzed}} surveys analyzed &middot; {{prev_output.total_responses_analyzed}} total responses",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:28px;"><tr>
      ${_metricCard("Surveys", "{{prev_output.surveys_analyzed}}", "#6366f1")}
      ${_metricCard("Responses", "{{prev_output.total_responses_analyzed}}", "#10b981")}
    </tr></table>
    ${_section("Per-Survey Results", `<div>{{prev_output._items_html}}</div>`)}
    ${_summaryBox("Bulk sentiment analysis complete. {{prev_output.surveys_analyzed}} surveys were analyzed covering {{prev_output.total_responses_analyzed}} total responses. Review individual survey results above for detailed sentiment breakdowns.")}`
  ),

  // ── Missing Opportunity Templates ──────────────────────────────────
  "crm.list_opportunities": _wrap(
    "linear-gradient(135deg,#8b5cf6,#7c3aed)", "💼", "Opportunity Report",
    "{{prev_output.count}} opportunities found",
    `<table width="100%" cellpadding="0" cellspacing="10" style="margin-bottom:24px;"><tr>
      ${_metricCard("Total Opportunities", "{{prev_output.count}}", "#8b5cf6")}
    </tr></table>
    ${_section("Opportunities", `<div>{{prev_output.items}}</div>`)}
    ${_summaryBox("Opportunity listing complete. {{prev_output.count}} opportunities matched the requested criteria. Review and advance promising deals through the pipeline.")}`
  ),

  "crm.update_opportunity": _wrap(
    "linear-gradient(135deg,#8b5cf6,#7c3aed)", "💼", "Opportunity Updated",
    "Opportunity #{{prev_output.opportunity_id}} updated",
    `${_section("Opportunity Details", `<div style="font-size:13px;line-height:2;color:#475569;">
      <div>
        <span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;">Opportunity ID</span>
        <span style="color:#334155;font-weight:500;margin-left:6px;">{{prev_output.opportunity_id}}</span>
      </div>
    </div>`)}
    <p style="font-size:14px;line-height:1.8;color:#334155;margin:0 0 16px;">
      The opportunity has been updated with the latest stage and details. Our team is actively working to advance this deal.
    </p>
    <div style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:10px;padding:16px;text-align:center;margin-top:20px;">
      <p style="margin:0;font-size:14px;font-weight:700;color:#5b21b6;">Deal pipeline updated.</p>
    </div>
    ${_summaryBox("Opportunity has been updated successfully. The deal stage and details are now current. Continue nurturing this opportunity through the pipeline.")}`
  ),
};

// Fallback template for actions not in the map
export const EMAIL_BODY_DEFAULT = _wrap(
  "linear-gradient(135deg,#64748b,#475569)", "📧", "Workflow Notification", null,
  `<p style="font-size:14px;line-height:1.8;color:#334155;margin:0 0 16px;">
    This is an automated notification from your workflow. The previous step has completed successfully.
  </p>
  <p style="font-size:14px;line-height:1.8;color:#334155;margin:0 0 16px;">
    Use <code style="background:#f1f5f9;padding:2px 6px;border-radius:4px;font-size:13px;">{{prev_output.field}}</code> placeholders to include dynamic data from the previous step.
  </p>
  ${_summaryBox("Workflow step completed. Review the results and take any necessary follow-up actions.")}`
);

/** Given the previous step's action_type, return the best email body HTML. */
export function getEmailBodyForAction(prevActionType) {
  return EMAIL_BODY_BY_ACTION[prevActionType] || EMAIL_BODY_DEFAULT;
}

// Resource categories for template grouping
export const TEMPLATE_CATEGORIES = [
  { key: "chat", label: "Chat", icon: "💬" },
  { key: "surveys", label: "Surveys", icon: "📊" },
  { key: "volunteers", label: "Volunteers", icon: "👥" },
  { key: "events", label: "Events", icon: "📅" },
  { key: "crm", label: "CRM", icon: "💼" },
];

const WORKFLOW_TEMPLATES = [
  {
    id: "approve_assign_volunteers",
    category: "volunteers",
    name: "Approve & Assign Volunteers",
    description:
      "Find pending volunteers, approve them, assign to an existing event, and send a welcome email.",
    agentGoal:
      "List all pending volunteers. Bulk approve them. Then assign each approved volunteer to the selected event. Finally, send each volunteer a welcome email with the event details.",
    allowedActions: [
      "volunteer.list",
      "volunteer.get",
      "volunteer.update",
      "volunteer.bulk_approve",
      "volunteer.assign_to_event",
      "volunteer.bulk_update",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Pending Volunteers",
        action_type: "volunteer.list",
        action_config: {
          status: "PENDING",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Bulk Approve Volunteers",
        action_type: "volunteer.bulk_approve",
        action_config: {
          volunteer_ids: [],
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Assign to Event",
        action_type: "volunteer.assign_to_event",
        action_config: {
          volunteer_id: "{{prev_output.approved_ids[0]}}",
          event_id: "",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
      {
        step_order: 3,
        name: "Send Welcome Email",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "{{prev_output.email}}",
          subject: "You're approved and assigned!",
          body: EMAIL_BODY_BY_ACTION["volunteer.assign_to_event"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "event_volunteer_outreach",
    category: "events",
    name: "Event Volunteer Outreach",
    description:
      "Fetch an existing event, list active volunteers, and launch an outreach campaign with email and SMS.",
    agentGoal:
      "Get the event details for the selected event. Then list all active volunteers. Create an outreach campaign for the event that sends both email and SMS invitations to the volunteers.",
    allowedActions: [
      "event.list",
      "event.get",
      "event.update",
      "event.send_invite",
      "event.outreach_campaign",
      "event.bulk_update",
      "volunteer.list",
      "volunteer.get",
      "volunteer.update",
      "volunteer.bulk_approve",
      "volunteer.assign_to_event",
      "volunteer.bulk_update",
      "comm.send_email_gmail",
      "comm.send_sms",
    ],
    steps: [
      {
        step_order: 0,
        name: "Get Event Details",
        action_type: "event.get",
        action_config: {
          event_id: "",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "List Active Volunteers",
        action_type: "volunteer.list",
        action_config: {
          status: "ACTIVE",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
      {
        step_order: 2,
        name: "Launch Outreach Campaign",
        action_type: "event.outreach_campaign",
        action_config: {
          event_id: "{{prev_output.id}}",
          campaign_name: "Outreach Campaign",
          send_email: true,
          send_sms: true,
          email_subject: "You're invited to our event!",
          email_body: "Join us at our upcoming event!",
          sms_message: "You're invited to our upcoming event!",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "lead_qualify_followup",
    category: "crm",
    name: "Lead Qualification & Follow-up",
    description:
      "List new leads, update their status to CONTACTED, and send a follow-up email.",
    agentGoal:
      "List all leads with status NEW. For each lead, update the status to CONTACTED. Then send each lead a follow-up email thanking them for their interest.",
    allowedActions: [
      "crm.list_leads",
      "crm.get_lead",
      "crm.update_lead",
      "crm.bulk_update_leads",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List New Leads",
        action_type: "crm.list_leads",
        action_config: {
          status: "NEW",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Update Lead Status",
        action_type: "crm.update_lead",
        action_config: {
          lead_id: "{{prev_output.items[0].id}}",
          status: "CONTACTED",
          qualification_notes: "Auto-contacted via workflow",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Send Follow-up Email",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "{{prev_output.email}}",
          subject: "Thanks for your interest!",
          body: EMAIL_BODY_BY_ACTION["crm.update_lead"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "bulk_approve_notify",
    category: "volunteers",
    name: "Bulk Approve & Notify",
    description:
      "List all pending volunteers, approve them, and send each a confirmation SMS.",
    agentGoal:
      "List all pending volunteers. Bulk approve all of them. Then send each approved volunteer an SMS confirming their approval.",
    allowedActions: [
      "volunteer.list",
      "volunteer.get",
      "volunteer.update",
      "volunteer.bulk_approve",
      "volunteer.assign_to_event",
      "volunteer.bulk_update",
      "comm.send_sms",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Pending Volunteers",
        action_type: "volunteer.list",
        action_config: {
          status: "PENDING",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Bulk Approve Volunteers",
        action_type: "volunteer.bulk_approve",
        action_config: {
          volunteer_ids: [],
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Send Confirmation SMS",
        action_type: "comm.send_sms",
        action_config: {
          to: "{{prev_output.phone}}",
          message:
            "Congratulations! You have been approved as a volunteer. Welcome to the team!",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "ticket_triage_notify",
    category: "crm",
    name: "Ticket Triage & Notify",
    description:
      "List open tickets, update priority and status, and send a notification email to the customer.",
    agentGoal:
      "List all open support tickets. For each ticket, update the status to IN_PROGRESS. Then send a notification email to the customer confirming their ticket is being worked on.",
    allowedActions: [
      "crm.list_tickets",
      "crm.get_ticket",
      "crm.update_ticket",
      "crm.bulk_update_tickets",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Open Tickets",
        action_type: "crm.list_tickets",
        action_config: {
          status: "OPEN",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Update Ticket Status",
        action_type: "crm.update_ticket",
        action_config: {
          ticket_id: "{{prev_output.items[0].id}}",
          status: "IN_PROGRESS",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Send Notification Email",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "{{prev_output.email}}",
          subject: "Your ticket is being reviewed",
          body: EMAIL_BODY_BY_ACTION["crm.update_ticket"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "survey_sentiment_analysis",
    category: "surveys",
    name: "Survey Sentiment Analysis & Notify",
    description:
      "Fetch an active survey, run sentiment analysis on responses, and email a summary of negative feedback to the team.",
    agentGoal:
      "Get the specified survey. Trigger sentiment analysis on all text responses. Get the sentiment results. Filter negative responses. Send an email summary of negative feedback to the team for follow-up.",
    allowedActions: [
      "survey.list",
      "survey.get",
      "survey.update",
      "survey.bulk_update",
      "survey.bulk_sentiment",
      "survey.list_responses",
      "survey.trigger_sentiment",
      "survey.get_sentiment",
      "survey.filter_by_sentiment",
      "survey.list_distributions",
      "survey.get_analytics",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "Get Survey Details",
        action_type: "survey.get",
        action_config: {
          survey_id: "",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Run Sentiment Analysis",
        action_type: "survey.trigger_sentiment",
        action_config: {
          survey_id: "{{prev_output.survey_id}}",
          force_reanalyze: "false",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
      {
        step_order: 2,
        name: "Get Sentiment Results",
        action_type: "survey.get_sentiment",
        action_config: {
          survey_id: "{{prev_output.survey_id}}",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
      {
        step_order: 3,
        name: "Email Sentiment Summary",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Sentiment Report",
          body: EMAIL_BODY_BY_ACTION["survey.get_sentiment"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "survey_negative_feedback_followup",
    category: "surveys",
    name: "Negative Feedback Follow-up",
    description:
      "Analyze survey sentiment, find negative responses, and send a follow-up email to unhappy respondents.",
    agentGoal:
      "Trigger sentiment analysis for the given survey. Then filter responses with NEGATIVE sentiment. For each negative respondent, send a personalized follow-up email acknowledging their feedback and offering support.",
    allowedActions: [
      "survey.list",
      "survey.get",
      "survey.update",
      "survey.bulk_update",
      "survey.bulk_sentiment",
      "survey.list_responses",
      "survey.trigger_sentiment",
      "survey.get_sentiment",
      "survey.filter_by_sentiment",
      "survey.list_distributions",
      "survey.get_analytics",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "Run Sentiment Analysis",
        action_type: "survey.trigger_sentiment",
        action_config: {
          survey_id: "",
          force_reanalyze: "false",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Filter Negative Responses",
        action_type: "survey.filter_by_sentiment",
        action_config: {
          survey_id: "{{prev_output.survey_id}}",
          sentiment: "NEGATIVE",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
      {
        step_order: 2,
        name: "Send Follow-up Email",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "{{prev_output.items[0].email}}",
          subject: "We value your feedback",
          body: EMAIL_BODY_BY_ACTION["survey.filter_by_sentiment"],
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
    ],
  },
  {
    id: "survey_analytics_report",
    category: "surveys",
    name: "Survey Analytics & Report",
    description:
      "Get survey analytics and sentiment data, then email a comprehensive report to stakeholders.",
    agentGoal:
      "Get analytics for the specified survey. Then get the sentiment analysis results. Compile the data and send a comprehensive report via email to the stakeholders.",
    allowedActions: [
      "survey.list",
      "survey.get",
      "survey.update",
      "survey.bulk_update",
      "survey.bulk_sentiment",
      "survey.list_responses",
      "survey.trigger_sentiment",
      "survey.get_sentiment",
      "survey.filter_by_sentiment",
      "survey.list_distributions",
      "survey.get_analytics",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "Get Survey Analytics",
        action_type: "survey.get_analytics",
        action_config: {
          survey_id: "",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Get Sentiment Results",
        action_type: "survey.get_sentiment",
        action_config: {
          survey_id: "{{prev_output.survey_id}}",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
      {
        step_order: 2,
        name: "Email Analytics Report",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Survey Analytics Report",
          body: EMAIL_BODY_BY_ACTION["survey.get_sentiment"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "crm_lead_bulk_qualify",
    category: "crm",
    name: "Lead Bulk Qualification & Outreach",
    description:
      "List NEW leads, bulk update them to QUALIFIED, and send a follow-up email.",
    agentGoal:
      "List all leads with status NEW. Bulk update all of them to QUALIFIED status. Then send each lead a follow-up email about next steps.",
    allowedActions: [
      "crm.list_leads",
      "crm.get_lead",
      "crm.update_lead",
      "crm.bulk_update_leads",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List New Leads",
        action_type: "crm.list_leads",
        action_config: {
          status: "NEW",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Bulk Qualify Leads",
        action_type: "crm.bulk_update_leads",
        action_config: {
          lead_ids: "{{prev_output.items.map(i => i.id)}}",
          status: "QUALIFIED",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Send Follow-up Email",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Your application has been qualified!",
          body: EMAIL_BODY_BY_ACTION["crm.bulk_update_leads"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "crm_contact_bulk_archive",
    category: "crm",
    name: "Contact Bulk Archive & Notify",
    description:
      "List INACTIVE contacts, bulk update them to ARCHIVED, and send a notification email.",
    agentGoal:
      "List all contacts with status INACTIVE. Bulk update all of them to ARCHIVED status. Then send a notification email about the archival.",
    allowedActions: [
      "crm.list_contacts",
      "crm.get_contact",
      "crm.update_contact",
      "crm.bulk_update_contacts",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Inactive Contacts",
        action_type: "crm.list_contacts",
        action_config: {
          status: "INACTIVE",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Bulk Archive Contacts",
        action_type: "crm.bulk_update_contacts",
        action_config: {
          contact_ids: "{{prev_output.items.map(i => i.id)}}",
          status: "ARCHIVED",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Send Archive Notification",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Contacts Archived",
          body: EMAIL_BODY_BY_ACTION["crm.bulk_update_contacts"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "crm_ticket_bulk_escalate",
    category: "crm",
    name: "Ticket Bulk Escalation",
    description:
      "List OPEN HIGH priority tickets, bulk update them to ESCALATED, and send an alert email.",
    agentGoal:
      "List all open tickets with HIGH priority. Bulk update all of them to ESCALATED status. Then send an alert email to the team about the escalation.",
    allowedActions: [
      "crm.list_tickets",
      "crm.get_ticket",
      "crm.update_ticket",
      "crm.bulk_update_tickets",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Open High Priority Tickets",
        action_type: "crm.list_tickets",
        action_config: {
          status: "OPEN",
          priority: "HIGH",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Bulk Escalate Tickets",
        action_type: "crm.bulk_update_tickets",
        action_config: {
          ticket_ids: "{{prev_output.items.map(i => i.id)}}",
          status: "ESCALATED",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Send Escalation Alert",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Tickets Escalated — Immediate Attention Required",
          body: EMAIL_BODY_BY_ACTION["crm.bulk_update_tickets"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "crm_opportunity_pipeline_advance",
    category: "crm",
    name: "Opportunity Pipeline Advance",
    description:
      "List PROPOSAL stage opportunities, bulk update them to NEGOTIATION, and send an email update.",
    agentGoal:
      "List all opportunities in PROPOSAL stage. Bulk update all of them to NEGOTIATION stage. Then send an email update about the pipeline advancement.",
    allowedActions: [
      "crm.list_opportunities",
      "crm.update_opportunity",
      "crm.bulk_update_opportunities",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Proposal Opportunities",
        action_type: "crm.list_opportunities",
        action_config: {
          stage: "PROPOSAL",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Advance to Negotiation",
        action_type: "crm.bulk_update_opportunities",
        action_config: {
          opportunity_ids: "{{prev_output.items.map(i => i.id)}}",
          stage: "NEGOTIATION",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Send Pipeline Update Email",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Opportunities Advanced to Negotiation",
          body: EMAIL_BODY_BY_ACTION["crm.bulk_update_opportunities"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "event_bulk_complete_notify",
    category: "events",
    name: "Event Bulk Complete & Notify",
    description:
      "List ONGOING events, bulk update them to COMPLETED, and send a completion email.",
    agentGoal:
      "List all events with status ONGOING. Bulk update all of them to COMPLETED status. Then send a completion notification email.",
    allowedActions: [
      "event.list",
      "event.get",
      "event.update",
      "event.send_invite",
      "event.outreach_campaign",
      "event.bulk_update",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Ongoing Events",
        action_type: "event.list",
        action_config: {
          status: "ONGOING",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Bulk Complete Events",
        action_type: "event.bulk_update",
        action_config: {
          event_ids: "{{prev_output.items.map(i => i.id)}}",
          status: "COMPLETED",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Send Completion Email",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Events Completed Successfully",
          body: EMAIL_BODY_BY_ACTION["event.bulk_update"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "volunteer_bulk_deactivate_notify",
    category: "volunteers",
    name: "Volunteer Bulk Deactivate & Notify",
    description:
      "List ACTIVE volunteers, bulk update them to INACTIVE, and send an SMS notification.",
    agentGoal:
      "List all volunteers with status ACTIVE. Bulk update all of them to INACTIVE status. Then send each volunteer an SMS notification about their deactivation.",
    allowedActions: [
      "volunteer.list",
      "volunteer.get",
      "volunteer.update",
      "volunteer.bulk_approve",
      "volunteer.assign_to_event",
      "volunteer.bulk_update",
      "comm.send_sms",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Active Volunteers",
        action_type: "volunteer.list",
        action_config: {
          status: "ACTIVE",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Bulk Deactivate Volunteers",
        action_type: "volunteer.bulk_update",
        action_config: {
          volunteer_ids: "{{prev_output.items.map(i => i.id)}}",
          status: "INACTIVE",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Send Deactivation SMS",
        action_type: "comm.send_sms",
        action_config: {
          to: "{{prev_output.phone}}",
          message:
            "Your volunteer status has been updated to inactive. Thank you for your service. Contact us if you have questions.",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "survey_bulk_close_report",
    category: "surveys",
    name: "Survey Bulk Close & Report",
    description:
      "List ACTIVE surveys, bulk update them to CLOSED, get analytics, and send an email report.",
    agentGoal:
      "List all active surveys. Bulk update all of them to CLOSED status. Get analytics for the surveys. Then send a comprehensive report via email.",
    allowedActions: [
      "survey.list",
      "survey.get",
      "survey.update",
      "survey.bulk_update",
      "survey.bulk_sentiment",
      "survey.list_responses",
      "survey.trigger_sentiment",
      "survey.get_sentiment",
      "survey.filter_by_sentiment",
      "survey.list_distributions",
      "survey.get_analytics",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Active Surveys",
        action_type: "survey.list",
        action_config: {
          status: "ACTIVE",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Bulk Close Surveys",
        action_type: "survey.bulk_update",
        action_config: {
          survey_ids: "{{prev_output.items.map(i => i.id)}}",
          status: "CLOSED",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Get Survey Analytics",
        action_type: "survey.get_analytics",
        action_config: {
          survey_id: "{{prev_output.updated_ids[0]}}",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
      {
        step_order: 3,
        name: "Send Report Email",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Survey Closure Report",
          body: EMAIL_BODY_BY_ACTION["survey.get_analytics"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "survey_bulk_sentiment_report",
    category: "surveys",
    name: "Bulk Survey Sentiment Analysis & Report",
    description:
      "List ACTIVE surveys, run bulk sentiment analysis on all, and send a comprehensive sentiment report email.",
    agentGoal:
      "List all active surveys. Run bulk sentiment analysis on all active surveys. Then send a comprehensive sentiment report via email summarizing the findings across all surveys.",
    allowedActions: [
      "survey.list",
      "survey.get",
      "survey.update",
      "survey.bulk_update",
      "survey.bulk_sentiment",
      "survey.list_responses",
      "survey.trigger_sentiment",
      "survey.get_sentiment",
      "survey.filter_by_sentiment",
      "survey.list_distributions",
      "survey.get_analytics",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Active Surveys",
        action_type: "survey.list",
        action_config: {
          status: "ACTIVE",
        },
        condition: null,
      },
      {
        step_order: 1,
        name: "Run Bulk Sentiment Analysis",
        action_type: "survey.bulk_sentiment",
        action_config: {
          survey_ids: "{{prev_output.items.map(i => i.id)}}",
          force_reanalyze: "false",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Send Sentiment Report Email",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Bulk Sentiment Analysis Report",
          body: EMAIL_BODY_BY_ACTION["survey.bulk_sentiment"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  // ── Chat Templates ────────────────────────────────────────────────────
  {
    id: "chat_history_sentiment_report",
    category: "chat",
    name: "Chat History Sentiment & Report",
    description:
      "Analyze chatbot conversation history for sentiment and email a comprehensive report.",
    agentGoal:
      "List all chat history conversations. Trigger sentiment analysis on the chat history. Get the sentiment results. Then send a comprehensive sentiment report via email.",
    allowedActions: [
      "chat.list_history",
      "chat.get_history",
      "chat.list_feedbacks",
      "chat.trigger_history_sentiment",
      "chat.trigger_feedback_sentiment",
      "chat.get_sentiment",
      "chat.filter_by_sentiment",
      "chat.get_analytics",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List Chat History",
        action_type: "chat.list_history",
        action_config: {},
        condition: null,
      },
      {
        step_order: 1,
        name: "Run Sentiment Analysis",
        action_type: "chat.trigger_history_sentiment",
        action_config: {
          force_reanalyze: "false",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Get Sentiment Results",
        action_type: "chat.get_sentiment",
        action_config: {
          analysis_type: "history",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
      {
        step_order: 3,
        name: "Email Sentiment Report",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Chat History Sentiment Report",
          body: EMAIL_BODY_BY_ACTION["chat.get_sentiment"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "user_feedback_analysis",
    category: "chat",
    name: "User Feedback Analysis",
    description:
      "Analyze user feedback from chatbot conversations for sentiment and email a report.",
    agentGoal:
      "List all user feedbacks. Trigger sentiment analysis on the feedbacks. Get the sentiment results. Then send a comprehensive report via email.",
    allowedActions: [
      "chat.list_history",
      "chat.get_history",
      "chat.list_feedbacks",
      "chat.trigger_history_sentiment",
      "chat.trigger_feedback_sentiment",
      "chat.get_sentiment",
      "chat.filter_by_sentiment",
      "chat.get_analytics",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "List User Feedbacks",
        action_type: "chat.list_feedbacks",
        action_config: {},
        condition: null,
      },
      {
        step_order: 1,
        name: "Run Feedback Sentiment Analysis",
        action_type: "chat.trigger_feedback_sentiment",
        action_config: {
          force_reanalyze: "false",
        },
        condition: { field: "prev_output.count", op: "gt", value: "0" },
      },
      {
        step_order: 2,
        name: "Get Sentiment Results",
        action_type: "chat.get_sentiment",
        action_config: {
          analysis_type: "feedback",
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
      {
        step_order: 3,
        name: "Email Feedback Report",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "User Feedback Sentiment Report",
          body: EMAIL_BODY_BY_ACTION["chat.get_sentiment"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
  {
    id: "chat_analytics_report",
    category: "chat",
    name: "Chat Analytics Report",
    description:
      "Get chatbot analytics (sessions, Q&A pairs, training status, feedbacks) and email the report.",
    agentGoal:
      "Get the chat analytics for the chatbot including total sessions, Q&A pairs, training status, and feedback counts. Then send a comprehensive analytics report via email.",
    allowedActions: [
      "chat.list_history",
      "chat.get_history",
      "chat.list_feedbacks",
      "chat.trigger_history_sentiment",
      "chat.trigger_feedback_sentiment",
      "chat.get_sentiment",
      "chat.filter_by_sentiment",
      "chat.get_analytics",
      "comm.send_email_gmail",
    ],
    steps: [
      {
        step_order: 0,
        name: "Get Chat Analytics",
        action_type: "chat.get_analytics",
        action_config: {},
        condition: null,
      },
      {
        step_order: 1,
        name: "Email Analytics Report",
        action_type: "comm.send_email_gmail",
        action_config: {
          to: "",
          subject: "Chat Analytics Report",
          body: EMAIL_BODY_BY_ACTION["chat.get_analytics"],
        },
        condition: { field: "prev_output.success", op: "eq", value: true },
      },
    ],
  },
];

export default WORKFLOW_TEMPLATES;
